import * as React from "react"
import {
  Card, CardHeader, CardContent, Typography, Grid, List, ListItemButton, ListItemText,
  Chip, TextField, Button, Stack, Divider, Box, LinearProgress, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material"
import SaveIcon from "@mui/icons-material/Save"
import DeleteIcon from "@mui/icons-material/Delete"
import { api } from "../../function/axios"

type PromptItem = {
  id: number
  name: string
  updatedAt?: string
  text: string
  active?: boolean
}

export default function PromptsPanel() {
  const [items, setItems] = React.useState<PromptItem[]>([])
  const [selected, setSelected] = React.useState<string>("")
  const [loading, setLoading] = React.useState<boolean>(true)
  const [saving, setSaving] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [info, setInfo] = React.useState<string | null>(null)

  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [deleteTarget, setDeleteTarget] = React.useState<PromptItem | null>(null)

  const current = React.useMemo(() => items.find(i => i.name === selected), [items, selected])
  const activeName = React.useMemo(() => items.find(i => i.active)?.name ?? "", [items])

  const refetchPrompts = React.useCallback(
    async (keepSelected?: string) => {
      const res = await api({ method: "GET", url: "/ai-assistant/prompts" })
      if (!res.code) throw new Error(`GET prompts → ${res.code}`)

      const data: Array<{
        id: number
        name: string
        preview: string
        updated_at?: string
        active?: boolean
      }> = await res?.data

      const mapped: PromptItem[] = data.map(d => ({
        id: Number(d.id),
        name: d.name,
        updatedAt: d.updated_at ? `Updated ${new Date(d.updated_at).toLocaleString()}` : undefined,
        text: d.preview ?? "",
        active: !!d.active,
      }))

      if (!mapped.some(m => m.active) && mapped.length > 0) {
        const idx = mapped.findIndex(m => m.name === "default")
        if (idx >= 0) mapped[idx].active = true
        else mapped[0].active = true
      }

      setItems(mapped)
      const desired = keepSelected || mapped.find(m => m.active)?.name || mapped[0]?.name || ""
      setSelected(mapped.some(m => m.name === desired) ? desired : (mapped[0]?.name ?? ""))
    },
    [],
  )

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      setSaving(true)
      setError(null)
      await api({
        method: "DELETE",
        url: `/ai-assistant/prompt/${deleteTarget.id}`,
      })
      await refetchPrompts(selected === deleteTarget.name ? undefined : selected)
      setInfo(`Prompt removed: ${deleteTarget.name}`)
    } catch (err: any) {
      setError(err.message ?? String(err))
    } finally {
      setSaving(false)
      setConfirmOpen(false)
      setDeleteTarget(null)
    }
  }

  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        await refetchPrompts()
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [refetchPrompts])

  const updateText = (value: string) => {
    setItems(prev => prev.map(i => (i.name === selected ? { ...i, text: value } : i)))
  }

  const updateName = (value: string) => {
    setItems(prev => prev.map(i => (i.name === selected ? { ...i, name: value } : i)))
    setSelected(value)
  }

  const putPrompt = async (setActive: boolean) => {
    if (!current) return
    setSaving(true)
    setError(null)
    setInfo(null)
    try {
      const payload = {
        name: current.name,
        content: current.text ?? "",
        setActive,
      }
      const res = await api({
        method: "PUT",
        url: "/ai-assistant/prompt",
        headers: { "Content-Type": "application/json" },
        body: payload,
      })
      if (!res.code) throw new Error(`PUT prompt → ${res.code}`)

      await refetchPrompts(current.name)
      setInfo(setActive ? `Prompt saved and set as active` : `Prompt saved: ${current.name}`)
    } catch (e: any) {
      setError(e.message ?? String(e))
    } finally {
      setSaving(false)
    }
  }

  const handleSave = () => void putPrompt(false)
  const handleSetActive = () => {
    if (selected === activeName) return
    void putPrompt(true)
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        boxShadow: 0,
        borderColor: theme => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "divider"),
      }}
    >
      <CardHeader
        title={<Typography variant="subtitle1" fontWeight={600}>Prompts</Typography>}
        subheader={<Typography variant="caption" color="text.secondary">Edit, rename, and activate a system prompt</Typography>}
        sx={{ pb: 0.5 }}
      />
      {(loading || saving) && <LinearProgress />}
      <Divider />

      <CardContent sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ mb: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {info && <Alert severity="success">{info}</Alert>}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={3}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Prompt library
            </Typography>

            <List
              disablePadding
              sx={{
                bgcolor: "action.hover",
                borderRadius: 2,
                p: 1,
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {items.map((p) => (
                <Box key={p.id} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <ListItemButton
                    selected={selected === p.name}
                    onClick={() => setSelected(p.name)}
                    sx={{ borderRadius: 2, mb: 1, alignItems: "flex-start" }}
                    disabled={loading || saving}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Tooltip title={p.name.length > 20 ? p.name : ""} arrow placement="top">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              sx={{ maxWidth: 140 }}
                            >
                              {p.name.length > 20 ? `${p.name.slice(0, 20)}…` : p.name}
                            </Typography>
                          </Tooltip>

                          {p.active && (
                            <Chip
                              size="small"
                              label="active"
                              color="success"
                              variant="filled"
                              sx={{ height: 22 }}
                            />
                          )}
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                          {p.updatedAt || ""}
                        </Typography>
                      }
                    />
                  </ListItemButton>

                  {!p.active && (
                    <Button
                      variant="contained"
                      color="error"
                      style={{
                        width: 27,
                        height: 27,
                        minWidth: 0,
                        borderRadius: "50%",
                        padding: 0,
                      }}
                      disabled={loading || saving}
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteTarget(p)
                        setConfirmOpen(true)
                      }}
                    >
                      <DeleteIcon style={{ width: 20, height: 20 }} />
                    </Button>
                  )}
                </Box>
              ))}
            </List>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Stack spacing={2}>
              <TextField
                label="Prompt name"
                variant="outlined"
                value={current?.name ?? ""}
                onChange={(e) => updateName(e.target.value)}
                fullWidth
                disabled={!current || loading || saving}
              />

              <TextField
                placeholder="System prompt"
                variant="outlined"
                value={current?.text ?? ""}
                onChange={(e) => updateText(e.target.value)}
                multiline
                fullWidth
                minRows={5}
                maxRows={20}
                sx={{
                  height: "70%",
                  "& .MuiInputBase-root": {
                    height: "100%",
                    alignItems: "flex-start",
                  },
                }}
              />

              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{ textTransform: "none" }}
                  disabled={!current || loading || saving}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  disabled={!current || selected === activeName || loading || saving}
                  onClick={handleSetActive}
                  sx={{ textTransform: "none" }}
                >
                  Set Active
                </Button>

                <Box sx={{ ml: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Name: <Box component="span" sx={{ color: "text.primary" }}>{selected || "—"}</Box>
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>

      {/* Диалог подтверждения */}
      <Dialog
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false)
          setDeleteTarget(null)
        }}
      >
        <DialogTitle>Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <b>{deleteTarget?.name}</b> prompt?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmOpen(false)
              setDeleteTarget(null)
            }}
          >
            No
          </Button>
          <Button color="error" onClick={handleDelete} disabled={saving}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
